/**
 * Light document-id helpers for Panama (demo-grade, forgiving by design — the
 * secretary persona should never be hard-blocked by formatting).
 */
import { formatIdNumber } from '@cumpli/shared';
import type { Persona } from './phase1';

/**
 * Format a natural-person cédula as the user types: optional letter prefix
 * (PE/E/N/AV…) then numeric groups joined by dashes (provincia-tomo-asiento).
 * If the input is already segmented with dashes, its grouping is honored as-is
 * (letter prefixes and two-digit provinces 10–13 only survive when dashed).
 * A bare, dashless run biases provincia to a SINGLE digit so it never yields a
 * two-digit first group. Examples: "8991536" → "8-9915-36", "E8991" → "E-8-991",
 * "8123-4567" → "8123-4567" (kept), "E-8-56789" → "E-8-56789" (kept).
 *
 * Panama-only shim over the per-country `formatIdNumber` in `@cumpli/shared` —
 * kept so existing callers (and this file's own tests) don't need to know about
 * the country parameter. Byte-for-byte identical to `formatIdNumber('PAN', raw)`.
 */
export function formatCedula(raw: string): string {
  return formatIdNumber('PAN', raw);
}

/** RUC is structurally varied — keep it light: uppercase, allow digits/dashes. */
export function formatRuc(raw: string): string {
  return raw.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 20);
}

/** Strip to the comparable core (drop "Cédula "/"RUC " prefixes, spaces, case). */
export function normalizeDocId(raw: string): string {
  return raw.toLowerCase().replace(/c[ée]dula|ruc|pasaporte/g, '').replace(/[^0-9a-z]/g, '');
}

/** Soft validity check — used only for a gentle hint, never to block. */
export function looksValidDocId(raw: string, persona: Persona): boolean {
  const core = normalizeDocId(raw);
  if (core.length < 4) return false;
  return persona === 'legal' ? /\d/.test(core) : /^[a-z]{0,3}\d{3,}/.test(core);
}
